import { useQuery } from '@atlassian/conversation-assistant-service/common/utils/use-query';
import { performGqlFetch } from '@atlassian/conversation-assistant-utils/graphqlUtils';

import { isValidImpactedWorkUrl } from './is-valid-impacted-work-url';

/**
 * AGG node query: resolves an ARI to its web URL.
 * Supports JiraIssue (webUrl), TownsquareProject (url), JiraAlignAggProject (mercuryProjectUrl).
 */
const RESOLVE_ARI_TO_URL_QUERY = `
	query useResolveImpactedWorkInput($ari: ID!) {
		node(id: $ari) {
			id
			__typename
			... on JiraIssue {
				webUrl
			}
			... on TownsquareProject {
				url
			}
			... on JiraAlignAggProject {
				mercuryProjectUrl
			}
		}
	}
`;

type ResolveAriToUrlResponse = {
	node: {
		id: string;
		__typename: string;
		webUrl?: string;
		url?: string;
		mercuryProjectUrl?: string;
	} | null;
};

type Input = {
	ari?: string | null;
	link?: string | null;
};

/**
 * Resolves impacted work input to a validated { ari, link } pair.
 *
 * Link path (link is present): sends the URL to object-resolver → returns { ari, link }.
 * ARI path (only ari present, no link): queries AGG node → returns { ari, link }.
 *
 * Always prefers the link path when a link is available.
 */
export const useResolveImpactedWorkInput = ({
	ari,
	link,
}: Input): {
	ari: string | null;
	link: string | null;
	isFetching: boolean;
	error: Error | null;
} => {
	const useLinkPath = isValidImpactedWorkUrl(link ?? '');
	const useAriPath = !useLinkPath && !!ari;

	// Link path: resolve URL → ARI + link via object-resolver.
	const {
		data: urlData,
		isFetching: urlFetching,
		error: urlError,
	} = useQuery({
		queryKey: ['impacted-work-resolver', link ?? ''],
		enabled: useLinkPath,
		queryFn: async () => {
			const response = await fetch('/gateway/api/object-resolver/resolve', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
				body: JSON.stringify({ resourceUrl: link }),
			});
			if (response.status === 404) throw new Error('Object resolver returned 404');
			if (!response.ok) throw new Error(`${response.status}: Failed to resolve impacted work`);
			const json = (await response.json()) as {
				meta?: { access?: string };
				data: { 'atlassian:ari'?: string; url?: string };
			};
			if (json.meta?.access !== 'granted') throw new Error('Access denied');
			return json;
		},
	});

	// ARI path: resolve ARI → URL via AGG node query.
	const {
		data: ariData,
		isFetching: ariFetching,
		error: ariError,
	} = useQuery({
		queryKey: ['focus-ask-resolve-ari-to-url', ari ?? ''],
		enabled: useAriPath,
		queryFn: () =>
			performGqlFetch<ResolveAriToUrlResponse, { ari: string }>({
				gqlQuery: RESOLVE_ARI_TO_URL_QUERY,
				variables: { ari: ari ?? '' },
			}),
	});

	if (useLinkPath) {
		return {
			ari: urlData?.data?.['atlassian:ari'] ?? null,
			link: urlData?.data?.url ?? null,
			isFetching: urlFetching,
			error: urlError,
		};
	}

	if (useAriPath) {
		const node = ariData?.data?.node;
		const resolvedUrl = node?.webUrl ?? node?.url ?? node?.mercuryProjectUrl ?? null;
		return {
			ari: ari ?? null,
			link: resolvedUrl,
			isFetching: ariFetching,
			error: ariError ?? null,
		};
	}

	return { ari: null, link: null, isFetching: false, error: null };
};
